import {ReportDefinitionV1} from './contracts/report-definition.types';
import {fieldRootOf, grainRootOf} from './data/definitionFields';

/** The grains this builder can build.
 *
 * Deliberately narrower than the contract's `reportType` enum. The other three values name
 * reports that have their own pages and that the builder does not author, so they resolve to no
 * grain at all rather than being coerced into one. */
export type ReportGrain = 'TASK_REPORT' | 'PROJECT_STATUS' | 'TIME_REG_AUDIT_LOG';

/** The contract's own `reportType` values, with the optionality stripped. Anchoring the display
 * names to this rather than to `string` is what makes a mistyped display name a compile error. */
type ReportTypeDisplayName = NonNullable<ReportDefinitionV1['reportType']>;

/** Taxonomy token -> the definition `reportType` display string.
 *
 * Two vocabularies that are NOT derivable from each other. 'Project Status' -> 'PROJECT_STATUS'
 * happens to be a plain uppercase; 'Task report' -> 'TASK_REPORT' is not, and report-service's
 * `ReportType` carries an explicit comment that its casing inconsistency is load-bearing (the
 * display string is matched as a raw string against the stored `type` column). So this is a
 * literal map — never rebuild either side with toUpperCase()/toLowerCase().
 *
 * Typed to the contract's own union rather than `string`: report-service compares this value
 * raw against a stored column, so a well-meaning casing tidy-up here would compile, pass tsc and
 * pass these tests while silently writing a type no report is ever found by. Against the union,
 * the same typo is a TS2820 with the correct spelling suggested. */
const DISPLAY_NAME_BY_GRAIN: Record<ReportGrain, ReportTypeDisplayName> = {
	TASK_REPORT: 'Task report',
	PROJECT_STATUS: 'Project Status',
	TIME_REG_AUDIT_LOG: 'Time Approval Audit Log',
};

/** Field-tree root -> grain.
 *
 * report-service selects its resolver by matching the root field name against
 * `FieldResolver.getFieldEntityName()`, so the root is what actually decides which rows a
 * definition returns. That makes it the one grain signal that cannot be wrong, and the only
 * evidence a definition carries once its `reportType` has been stripped.
 *
 * A Map, not an object literal: field names come from an LLM and round-trip through storage
 * against a schema that constrains them to a bare string, so a field called `toString` or
 * `constructor` reaches this lookup. On an object literal that returns an inherited function —
 * truthy, so it would be returned as if it were a grain, and both consumers then fail silently
 * (a function does not survive JSON.stringify into the taxonomy query, and has no display name
 * at save). A Map only ever answers for keys actually put in it. */
const GRAIN_BY_FIELD_ROOT = new Map<string, ReportGrain>([
	['task', 'TASK_REPORT'],
	['taskTotals', 'TASK_REPORT'],
	['projectStatus', 'PROJECT_STATUS'],
	['timeRegAuditEvent', 'TIME_REG_AUDIT_LOG'],
]);

/** The `reportType` display string a grain is written as. Used at save, so a report is recorded
 * as the grain it was filtered as. */
export function displayNameOf(grain: ReportGrain): ReportTypeDisplayName {
	return DISPLAY_NAME_BY_GRAIN[grain];
}

/** Which grain a definition holds, by precedence.
 *
 * 1. An explicit `reportType` decides. One this builder does not build yields null, which gates
 *    the conditions editor instead of offering it the wrong grain's fields — the same
 *    fail-closed posture as `loadDefinition`'s viewerPermission narrowing.
 * 2. An absent `reportType` falls through to the field root. Absent is legitimate in two ways:
 *    every report predating the grain concept has none, and the drafting agent deliberately pops
 *    the field off a validation-failure fallback definition while leaving that grain's default
 *    columns in place.
 * 3. An unrecognised root, or no fields, defaults to task — the contract's established meaning
 *    of an absent reportType, and what `buildSaveReportAsInput` has always written.
 *
 * NOTE: this deliberately diverges from `forecast-ai-agents`' `grain.py`, which coerces an
 * unknown report type to TASK_REPORT. That is a producer choosing a value to emit, where an
 * invalid one kills the whole draft and there is no safe degraded state. This is a consumer
 * interpreting a value someone else chose, and it has one. Do not "fix" the inconsistency. */
export function grainOf(definition: Pick<ReportDefinitionV1, 'reportType' | 'fields'>): ReportGrain | null {
	const {reportType} = definition;
	if (reportType !== undefined && reportType !== null) {
		const grains = Object.keys(DISPLAY_NAME_BY_GRAIN) as ReportGrain[];
		return grains.find(grain => DISPLAY_NAME_BY_GRAIN[grain] === reportType) ?? null;
	}
	for (const field of definition.fields ?? []) {
		// Matched on the field's ROOT rather than its whole name, because a definition does not
		// always arrive as the tree the contract specifies: the drafting agent has been observed
		// emitting every column as one flat node whose name is the whole dotted path
		// (`projectStatus.stage`). Matching that verbatim finds nothing, so a project-status report
		// that carries no reportType read as a task report - which offers the wrong grain's filter
		// fields and saves it as the wrong type.
		// `fieldRootOf`, not `grainRootOf`: the latter answers `task` for a name that carries no
		// usable root, which is the right default for a whole definition and wrong here. Taken as
		// this field's answer it matches the task root and returns, so a malformed first field would
		// decide the grain and the project-status field behind it would never be read.
		const root = fieldRootOf(field.name);
		const grain = root === null ? undefined : GRAIN_BY_FIELD_ROOT.get(root);
		if (grain) {
			return grain;
		}
	}
	return 'TASK_REPORT';
}

/** The grain a definition's ROWS are keyed under, read from the field-tree root.
 *
 * Deliberately not `grainOf`, which gives the stored `reportType` precedence. The two disagree on a
 * real class of report: one saved before the grain was recorded carries `reportType: 'Task report'`
 * alongside `projectStatus` fields. Everything that produces or renders a row reads the root
 * instead - the mapper's cell keys, `groupingCellPaths`, `budgetTypePathFor`, and the taxonomy the
 * fetch is given (`rowsNeedTaxonomy` in `ReportBuilderContext`, which says the same thing at
 * length). So anything DESCRIBING those rows has to read the root too, or it can caption a table of
 * projects as tasks.
 *
 * Null when the root names no grain this builder knows. That is not the same as `grainOf`'s null:
 * this one is reachable, and it is the answer that keeps a caption honest - an unrecognised root
 * gets wording that names no entity rather than the task wording, which `grainRootOf`'s own
 * `task` default would otherwise supply. */
export function rowGrainOf(definition: Pick<ReportDefinitionV1, 'fields'>): ReportGrain | null {
	return GRAIN_BY_FIELD_ROOT.get(grainRootOf(definition)) ?? null;
}

/** The two message ids the row-count line above the table can be built from, for one grain. */
export interface RowCountMessages {
	/** The fetch was truncated: names the shown/matched counts and points at Export. */
	capped: string;
	/** The whole matched set is on screen: names one count and nothing about Export. */
	uncapped: string;
}

/** Grain -> the row-count line's message ids.
 *
 * Two ids per grain rather than one, because the truncated wording ends with "Export to get the
 * full set" and that is false on a result which was not truncated - the full set is already on
 * screen. A single string cannot say both.
 *
 * Whole sentences per grain, NOT one sentence with a `{entity}` hole. The line names what the
 * report lists ("tasks", "projects"), and an injected noun is unusable in the locales this app
 * ships: da/es/fr inflect it and give it a gender the surrounding words have to agree with, so a
 * hole would produce grammatical nonsense the moment anyone actually translates these. Per-grain
 * sentences also leave the plural rule inside the sentence, where ICU can apply the right one.
 *
 * A `Record<ReportGrain, ...>`, so a third grain added to the union is a compile error here until
 * it says what its rows are called - which is what stops it inheriting "tasks". */
export const ROW_COUNT_MESSAGES_BY_GRAIN: Record<ReportGrain, RowCountMessages> = {
	TASK_REPORT: {
		capped: 'ai_report_builder.row_count_capped.task',
		uncapped: 'ai_report_builder.row_count.task',
	},
	PROJECT_STATUS: {
		capped: 'ai_report_builder.row_count_capped.project_status',
		uncapped: 'ai_report_builder.row_count.project_status',
	},
	TIME_REG_AUDIT_LOG: {
		capped: 'ai_report_builder.row_count_capped.audit_event',
		uncapped: 'ai_report_builder.row_count.audit_event',
	},
};

/** The pair for a definition whose rows are keyed under a root this builder knows no name for.
 * Names no entity at all ("rows"), which is the only honest thing left to say. */
const ROW_COUNT_MESSAGES_GENERIC: RowCountMessages = {
	capped: 'ai_report_builder.row_count_capped',
	uncapped: 'ai_report_builder.row_count',
};

/** Which wording the row-count line above the table uses, for the grain the rows are keyed under.
 *
 * A null grain answers the generic pair rather than throwing or defaulting to a real grain. Pass it
 * `rowGrainOf`, whose null means "these rows sit under a root with no known name" - a state a
 * future grain reaches by existing before it is registered here. The failure mode has to be naming
 * nothing, never naming the wrong entity, which is exactly what a `?? 'TASK_REPORT'` at the call
 * site would do. */
export function rowCountMessagesOf(grain: ReportGrain | null): RowCountMessages {
	return grain === null ? ROW_COUNT_MESSAGES_GENERIC : ROW_COUNT_MESSAGES_BY_GRAIN[grain];
}
